import { AccountType } from 'further-types/investor';
import {
  InvestorAccessPermission,
  LpInvestorAccessPermissionGroupKey,
} from 'further-types/further-user';
import {
  CrsClassification,
  FatcaClassification,
} from 'further-types/lp/investor-tax-details';
import { CurrencyCode } from 'further-types/lp/vehicle';
import { Country } from 'further-ui/labels';
import { CalendarDay, CalendarTime } from 'further-ui/utils';
import {
  OnboardingStep,
  OnboardingStepStatus,
} from 'further-types/lp/onboarding';
import { LpEmailTemplate } from 'further-types/lp/communications';

export enum TabType {
  InvestorDetails = 'investor-details',
  InvestorJourney = 'investor-journey',
  Communications = 'communications',
  CommitmentDetails = 'commitment-details',
  Notes = 'notes',
}

export enum ApplicationDetail {
  TargetCommitment = 'target-commitment',
  LocalCurrency = 'local-currency',
}

export enum InvestorInformation {
  Core = 'core',
  BankDetails = 'bank-details',
  TaxInformation = 'tax-information',
}

export type VehicleApplication = {
  id: string;
  vehicleId: string;
  investorId: string;
  targetCommitment: number;
  commitmentAmount: number;
  investorLocalCurrency?: CurrencyCode;
  signatoriesRequired?: number;
  applicationSteps: Array<{
    step: OnboardingStep;
    status: OnboardingStepStatus | null;
    requiresApproval: boolean;
    canUnapprove: boolean;
    changeLog: Array<{
      status: OnboardingStepStatus;
      changedBy?: {
        firstName?: string;
        lastName?: string;
        profilePic?: string;
      };
      isApproval?: boolean;
      changedAt: CalendarTime;
    }>;
  }>;
  commitmentSettings: {
    isManuallyAddedCommitment: boolean;
    externalCommitmentId?: string;
    lpFeeTemplateId?: string;
    carriedInterestFeeTemplateId?: string;
    equalisationInterestFeeTemplateId?: string;
    commitmentDate?: CalendarDay;
    documents: Array<{
      path: string;
      name: string;
      previewUrl: string;
    }>;
  };
  commitment?: {
    id: string;
    totalCommitmentLcy: number;
    commitmentDate: CalendarDay;
    lpFeeTemplateId: string | null;
    externalCommitmentId?: string;
    carriedInterestFeeTemplateId: string | null;
    equalisationInterestFeeTemplateId: string | null;
    documents: Array<{
      name: string;
      previewUrl: string;
    }>;
  };
  invitationEmails: Array<{
    furtherUserId: string;
    email: LpEmailTemplate;
    sentAt: CalendarTime;
  }>;
  hasClosedCommitment: boolean;
};

export type InvestorAddress = {
  address1?: string;
  address2?: string;
  city?: string;
  postcode?: string;
  state?: string;
  country?: Country;
  hasBeenUsedForThreeYearsOrMore?: boolean;
};

export type InvestorUser = {
  id: string;
  firstName?: string;
  lastName?: string;
  email: string;
  permissions: Array<InvestorAccessPermission>;
  copyIntoSystemEmails: boolean;
  isDefaultUser: boolean;
  permissionsGroup: LpInvestorAccessPermissionGroupKey;
  firstInvite: CalendarTime | null;
  lastEmail: CalendarTime | null;
  lastLogin: CalendarTime | null;
};

export type BankDetailsType = {
  investorId: string;
  bankName: string;
  accountHolderName: string;
  bankAddress: string;
  bankAccountNumber: string;
  bankSortCode: string;
  swift?: string;
  iban?: string;
  additionalBankNumberOrCode?: string;
};

export type InvestorTaxDetails = {
  investorId: string;
  taxResidencies: Array<{
    country?: Country;
    state?: string;
    taxIdentificationNumber?: string;
  }>;
  fatcaClassification?: {
    classification: FatcaClassification;
  };
  crsClassification?: {
    classification: CrsClassification;
  };
  customRequestAnswers?: Array<{
    answerFiles?: Array<{ previewUrl: string; name: string }>;
  }>;
};

export type Investor = {
  id: string;
  investorAddedDate?: CalendarDay;
  addresses: Array<InvestorAddress>;
} & (
  | {
      accountType: AccountType.Individual;
      firstName: string;
      middleName?: string;
      lastName: string;
      phoneNumber?: string;
      sector?: string;
      jobTitle?: string;
      dateOfBirth?: CalendarDay;
      nationalities: Array<Country>;
    }
  | {
      accountType: AccountType.LegalEntity;
      registeredName: string;
      registrationDate?: CalendarDay;
      registrationState?: string;
      registrationNumber: string;
      registrationCountry?: Country;
      keyContactPhoneNumber: string;
    }
);
